<script setup lang="ts">
import DeleteEntityDialog from '@/common/components/DeleteEntityDialog.vue';
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import { useColumnSelector, type ColumnField } from 'shared/common/composables/useColumnSelector';
import { useColumnFilters } from 'shared/common/composables/useColumnFilters';
import ColumnFilterCell from 'shared/common/components/ColumnFilterCell.vue';
import { Button, Card, Column, DataTable, InputText, Menu, type DataTableRowDoubleClickEvent } from 'primevue';
import type { MenuItem } from 'primevue/menuitem';
import AuditDialog from 'shared/audit/components/AuditDialog.vue';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { usePackageTypeAuditApi } from '@/audit/api/packageTypeAuditApi';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { clearCorruptDataTableState } from 'shared/common/dataTableState';
import { useDataTableMultiSort } from 'shared/common/composables/useDataTableMultiSort';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { usePackageTypeStore } from 'shared/packages/stores/packageTypeStore';
import { usePackagingStore } from 'shared/packages/stores/packagingStore';
import { useRefreshPackageTypeStore } from '../commands/refreshPackageTypeStore';
import { useRefreshPackagingStore } from '../commands/refreshPackagingStore';
import EditPackageTypeDialog from './EditPackageTypeDialog.vue';
import { useDeletePackageType } from '../api/deletePackageType';

const selectedColumnsStorageKey = 'package-types-table-columns';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const pageTranslations = computed(() => translations.value.packageTypes);

const packageTypeStore = usePackageTypeStore();
const packagingStore = usePackagingStore();
const refreshPackageTypeStore = useRefreshPackageTypeStore();
const refreshPackagingStore = useRefreshPackagingStore();
const deletePackageType = useDeletePackageType();
const packageTypeAuditApi = usePackageTypeAuditApi();
const actionFailedAlert = useActionFailedAlert();

const selectedRow = ref<{ id: string } | null>(null);
const menuPackageTypeId = ref<string>();
const editDialog = useTemplateRef('edit-dialog');
const deleteDialog = useTemplateRef('delete-dialog');
const packageTypeMenu = useTemplateRef('package-type-menu');
const auditDialog = useTemplateRef('audit-dialog');

const columnDefinitions = computed<ColumnField[]>(() => [
  { field: 'name', header: translations.value.generic.name, filterType: 'text' },
  { field: 'pieces', header: translations.value.generic.amount, filterType: 'numeric' },
  { field: 'layers', header: translations.value.packages.fields.layers, filterType: 'numeric' },
  { field: 'piecesPerLayer', header: translations.value.packages.fields.piecesPerLayer, filterType: 'numeric' },
  {
    field: 'spacerHeight',
    header: `${translations.value.packages.fields.spacerHeight} (${translations.value.units.millimeters})`,
    filterType: 'numeric',
  },
  { field: 'packaging', header: translations.value.sideMenu.packaging, filterType: 'text' },
]);

const { orderedColumns, selectedColumns, setSelectedColumns, setOrder, reset: resetColumns } =
  useColumnSelector({ storageKey: selectedColumnsStorageKey, columns: columnDefinitions });

const rows = computed(() => {
  return packageTypeStore.sortedByName.map((item) => ({
    id: item.id,
    name: item.name,
    pieces: item.pieces,
    layers: item.layers,
    piecesPerLayer: item.piecesPerLayer,
    spacerHeight: item.spacerHeight,
    packaging: item.packagingIds
      .map((id) => packagingStore.getById(id)?.name)
      .filter((name): name is string => !!name)
      .join(', '),
  }));
});

const { filters, filteredCount, hasActiveFilters, clearFilters, onFilter } = useColumnFilters({
  columns: columnDefinitions,
  totalCount: () => rows.value.length,
});

// Clear corrupt persisted sort state before the DataTable restores it while rendering.
clearCorruptDataTableState('package-types-table-state');

onMounted(() => {
  refreshPackagingStore.initialize();
  refreshPackageTypeStore.initialize();
});

const { multiSortMeta, sortBadgeClass } = useDataTableMultiSort();

function onAdd() {
  editDialog.value?.show();
}

function onRowDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const packageType = ev.data as { id: string };
  selectedRow.value = { id: packageType.id };
  editDialog.value?.show(packageType.id);
}

const packageTypeMenuItems = computed<MenuItem[]>(() => [
  {
    label: translations.value.generic.edit,
    icon: 'pi pi-pencil',
    command() {
      const id = menuPackageTypeId.value;
      if (!id) return;
      editDialog.value?.show(id);
    },
  },
  {
    label: translations.value.generic.copy,
    icon: 'pi pi-copy',
    command() {
      const id = menuPackageTypeId.value;
      if (!id) return;
      editDialog.value?.showCopy(id);
    },
  },
  {
    label: translations.value.generic.delete,
    icon: 'pi pi-trash',
    command() {
      const id = menuPackageTypeId.value;
      if (!id) return;
      deleteDialog.value?.show(id);
    },
  },
  {
    label: translations.value.generic.audit,
    icon: 'pi pi-search',
    async command() {
      const id = menuPackageTypeId.value;
      if (!id) return;

      const response = await packageTypeAuditApi.execute({ id });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      auditDialog.value?.show({ audit: response.audit });
    },
  },
]);

function togglePackageTypeMenu(ev: Event, id: string) {
  menuPackageTypeId.value = id;
  packageTypeMenu.value?.toggle(ev);
}

function onSaved(id: string) {
  selectedRow.value = { id };
}

function onDeleted(id: string) {
  if (selectedRow.value?.id === id) {
    selectedRow.value = null;
  }
}

function getPackageTypeForDelete(id: string) {
  return Promise.resolve({
    id,
    name: packageTypeStore.getById(id)?.name ?? id,
  });
}
</script>

<template>
  <Card>
    <template #title>
      <div class="title-row">
        <span>{{ pageTranslations.packageTypes }}</span>
        <div class="actions">
          <span v-if="hasActiveFilters && filteredCount < rows.length" class="filtered-count">
            ({{ translations.dataTable.showing }} {{ filteredCount }} {{ translations.dataTable.of }} {{ rows.length }})
          </span>
          <Button v-if="hasActiveFilters" type="button" severity="secondary" size="small" icon="pi pi-filter-slash"
            :label="translations.dataTable.clearFilters" @click="clearFilters" />
          <Button :label="translations.generic.add" @click="onAdd"></Button>
          <InputText v-model="filters.global.value" :placeholder="translations.generic.search"></InputText>
        </div>
      </div>
    </template>
    <template #content>
      <DataTable
        v-model:selection="selectedRow"
        v-model:filters="filters"
        v-model:multiSortMeta="multiSortMeta"
        :class="sortBadgeClass"
        :value="rows"
        data-key="id"
        selectionMode="single"
        sort-mode="multiple"
        stateStorage="local"
        stateKey="package-types-table-state"
        filterDisplay="menu"
        @filter="onFilter"
        @row-dblclick="onRowDoubleClick"
      >
        <Column v-for="(col, index) of selectedColumns" :key="col.field + '_' + index" :field="col.field" :header="col.header" sortable
          :filter="!!col.filterType" :filterField="col.filterField ?? col.field"
          :showFilterMatchModes="col.showFilterMatchModes" :showFilterOperator="col.showFilterOperator"
          :dataType="col.filterType === 'numeric' ? 'numeric' : undefined">
          <template v-if="col.filterType" #filter="{ filterModel, filterCallback }">
            <ColumnFilterCell :filter-type="col.filterType" :filter-model="filterModel" :filter-callback="filterCallback"
              :options="col.filterOptions"
              :placeholder="translations.dataTable.filterBy + ' ' + col.header.toLocaleLowerCase()" />
          </template>
        </Column>
        <Column column-key="column-selector" class="w-12 !text-end">
          <template #header>
            <ColumnSelectorButton :columns="orderedColumns" :modelValue="selectedColumns"
              @update:modelValue="setSelectedColumns" @reorder="setOrder" @reset="resetColumns" />
          </template>
          <template #body="{ data }">
            <Button icon="pi pi-ellipsis-v" variant="text" severity="secondary" rounded
              @click="(ev) => togglePackageTypeMenu(ev, data.id)"></Button>
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>

  <EditPackageTypeDialog ref="edit-dialog" @saved="onSaved"></EditPackageTypeDialog>
  <DeleteEntityDialog
    ref="delete-dialog"
    :delete="(id) => deletePackageType.execute({ id })"
    :getById="getPackageTypeForDelete"
    :translate="(t) => t.packageTypes.deleteDialog"
    @deleted="onDeleted"
  ></DeleteEntityDialog>
  <AuditDialog ref="audit-dialog"></AuditDialog>
  <Menu ref="package-type-menu" :model="packageTypeMenuItems" :popup="true" />
</template>

<style scoped>
.title-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
}

.actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.filtered-count {
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}
</style>
